import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  Circle,
  ExternalLink,
  Gift,
  HandCoins,
  Lightbulb,
  Sparkles,
} from "lucide-react";
import { useOrg } from "../../../context/OrgContext";
import { useSubscription } from "../../../context/SubscriptionContext";

interface ChecklistStep {
  id: string;
  order: number;
  icon: "settings" | "period" | "inventory" | "crm" | "invoice";
  action?: string;
}

const STEPS: ChecklistStep[] = [
  { id: "settings", order: 1, icon: "settings", action: "/settings" },
  { id: "period", order: 2, icon: "period", action: "/accounting-periods" },
  { id: "inventory", order: 3, icon: "inventory", action: "/inventory" },
  { id: "crm", order: 4, icon: "crm", action: "/crm" },
  { id: "invoice", order: 5, icon: "invoice", action: "/sales" },
  { id: "pos", order: 6, icon: "invoice", action: "/pos" },
  { id: "purchases", order: 7, icon: "period", action: "/purchases" },
  { id: "ai", order: 8, icon: "settings", action: "/ai-advisor" },
  { id: "anomalies", order: 9, icon: "inventory", action: "/inventory/anomalies" },
];

const STEP_PROGRESS_KEY = "godo_quickstart_progress_v1";
const STEP_COMPLETED_KEY = "godo_quickstart_completed_v1";

const ICON_COLORS: Record<ChecklistStep["icon"], string> = {
  settings: "text-amber-600 bg-amber-100 ring-amber-200",
  period: "text-violet-600 bg-violet-100 ring-violet-200",
  inventory: "text-emerald-600 bg-emerald-100 ring-emerald-200",
  crm: "text-sky-600 bg-sky-100 ring-sky-200",
  invoice: "text-rose-600 bg-rose-100 ring-rose-200",
};

export interface QuickStartChecklistProps {
  onRefreshKpis?: () => void;
}

function getIcon(icon: ChecklistStep["icon"]) {
  switch (icon) {
    case "settings":
      return <Sparkles className="h-5 w-5" strokeWidth={2} />;
    case "period":
      return <HandCoins className="h-5 w-5" strokeWidth={2} />;
    case "inventory":
      return <Gift className="h-5 w-5" strokeWidth={2} />;
    case "crm":
      return <Lightbulb className="h-5 w-5" strokeWidth={2} />;
    case "invoice":
      return <CheckCircle2 className="h-5 w-5" strokeWidth={2} />;
  }
}

export function QuickStartChecklist({ onRefreshKpis }: QuickStartChecklistProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orgId } = useOrg();
  const { daysRemaining, isTrial } = useSubscription();

  const storageKey = useMemo(() => {
    const base = `${STEP_PROGRESS_KEY}_${orgId ?? "global"}`;
    return base;
  }, [orgId]);

  const dismissedKey = useMemo(
    () => `${STEP_COMPLETED_KEY}_${orgId ?? "global"}`,
    [orgId],
  );

  const [visible, setVisible] = useState(true);
  const [completed, setCompleted] = useState<Set<string>>(new Set());

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed)) {
          setCompleted(new Set(parsed.filter((x) => typeof x === "string")));
        }
      }
      const dismissed = window.localStorage.getItem(dismissedKey);
      if (dismissed === "1") setVisible(false);
    } catch {
      // noop
    }
  }, [storageKey, dismissedKey]);

  const persistCompleted = useCallback(
    (next: Set<string>) => {
      setCompleted(next);
      try {
        window.localStorage.setItem(
          storageKey,
          JSON.stringify(Array.from(next)),
        );
      } catch {
        // noop
      }
    },
    [storageKey],
  );

  function toggleStep(id: string) {
    const next = new Set(completed);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    persistCompleted(next);
    if (id === "invoice") onRefreshKpis?.();
  }

  function goStep(step: ChecklistStep) {
    if (!step.action) return;
    toggleStep(step.id);
    navigate(step.action);
  }

  function dismiss() {
    setVisible(false);
    try {
      window.localStorage.setItem(dismissedKey, "1");
    } catch {
      // noop
    }
  }

  if (!visible) return null;

  const progress = Math.round((completed.size / STEPS.length) * 100);
  const doneCount = completed.size;
  const allDone = doneCount === STEPS.length;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <header className="relative overflow-hidden rounded-t-2xl bg-gradient-to-br from-godo-navy via-[#0b4e9c] to-godo-accent px-6 py-5 text-white">
        <div className="absolute -top-6 ltr:-right-6 rtl:-left-6 h-24 w-24 rounded-full bg-white/10 blur-2xl" />
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold ring-1 ring-white/25">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
              {t("quickstart.badge")}
            </span>
            <h3 className="mt-2 truncate text-lg font-bold sm:text-xl">
              {t("quickstart.heading")}
            </h3>
            <p className="mt-1 text-xs leading-6 text-white/85 sm:text-sm">
              {t("quickstart.subheading")}
            </p>
          </div>

          {isTrial && (
            <div className="shrink-0 text-left rtl:text-right">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-white/70">
                {t("quickstart.trial_label")}
              </p>
              <p className="mt-1 text-2xl font-black tracking-tight text-white">
                {Math.max(0, daysRemaining)}
                <span className="ms-1 text-sm font-semibold text-white/80">
                  {t("quickstart.days_unit")}
                </span>
              </p>
            </div>
          )}
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-white/90">
              {t("quickstart.progress_label")}
            </span>
            <span dir="ltr" className="rounded-full bg-white/15 px-2 py-0.5 font-mono text-[11px] font-semibold ring-1 ring-white/20">
              {doneCount} / {STEPS.length} · {progress}%
            </span>
          </div>
          <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-white/15 ring-1 ring-white/15">
            <div
              className="h-full rounded-full bg-gradient-to-l from-emerald-300 to-emerald-400 transition-all duration-500"
              style={{ width: `${progress}%` }}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>
      </header>

      <ol className="divide-y divide-slate-100 px-4 py-3 sm:px-5">
        {STEPS.map((step) => {
          const isDone = completed.has(step.id);
          return (
            <li
              key={step.id}
              className={`grid grid-cols-[auto_1fr_auto] items-center gap-3 py-3 transition ${
                isDone ? "opacity-80" : ""
              }`}
            >
              <button
                type="button"
                aria-pressed={isDone}
                onClick={() => toggleStep(step.id)}
                className={`shrink-0 rounded-full transition ${
                  isDone ? "text-emerald-500" : "text-slate-300 hover:text-slate-400"
                }`}
              >
                {isDone ? (
                  <CheckCircle2 className="h-6 w-6" strokeWidth={2.5} fill="currentColor" />
                ) : (
                  <Circle className="h-6 w-6" strokeWidth={2} />
                )}
              </button>

              <button
                type="button"
                onClick={() => goStep(step)}
                className="flex min-w-0 items-center gap-3 rounded-lg p-1 text-right transition hover:bg-slate-50 ltr:text-left"
              >
                <span
                  className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ${
                    ICON_COLORS[step.icon]
                  }`}
                >
                  {getIcon(step.icon)}
                </span>
                <span className="min-w-0 flex-1">
                  <p
                    className={`truncate text-sm font-semibold ${
                      isDone ? "text-slate-500 line-through decoration-slate-300" : "text-slate-800"
                    }`}
                  >
                    {t(`quickstart.step_${step.id}_title`)}
                  </p>
                  <p className="mt-0.5 truncate text-xs leading-5 text-slate-500">
                    {t(`quickstart.step_${step.id}_desc`)}
                  </p>
                </span>
              </button>

              <button
                type="button"
                onClick={() => goStep(step)}
                className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:border-godo-accent/60 hover:bg-godo-accent/5 hover:text-godo-accent"
              >
                {t("quickstart.action_open")}
                <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
              </button>
            </li>
          );
        })}
      </ol>

      <footer className="flex flex-col-reverse items-stretch gap-2 border-t border-slate-100 bg-slate-50/70 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[11px] text-slate-400">
          {allDone ? t("quickstart.all_done_hint") : t("quickstart.hide_hint")}
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setCompleted(new Set());
              persistCompleted(new Set());
            }}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-medium text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
          >
            {t("quickstart.reset")}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-slate-800"
          >
            {allDone ? t("quickstart.complete_close") : t("quickstart.dismiss")}
          </button>
        </div>
      </footer>
    </section>
  );
}
